import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/Home.vue'
import { useAuthStore } from '../stores/auth.store'
import {
    forceReloadDueToStaleAssets,
    isStaleModuleImportError,
} from '@/utils/stale-client.js'
import { trackEvent } from '@/utils/tracking.js'
import firebase from 'firebase/compat/app'
import * as authUtils from '@/utils/auth.js'

async function waitForAppInitialization(authStore, timeoutMs = 5000) {
    const startTime = Date.now()
    while (
        !authStore.state.appInitialized &&
        Date.now() - startTime < timeoutMs
    ) {
        await new Promise((resolve) => setTimeout(resolve, 100))
    }

    if (!authStore.state.appInitialized) {
        console.warn(
            `waitForAppInitialization timed out after ${timeoutMs}ms, proceeding anyway`
        )
    }
}

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
        // Root route - Authentication page
        {
            path: '/',
            name: 'auth',
            component: Home,
            meta: {
                requiresAuth: false,
                noAuth: true,
            },
            beforeEnter: (to, from, next) => {
                // Import auth store
                const authStore = useAuthStore()

                // If we're in the middle of logging out, always proceed to auth page
                if (authStore.state.isLoggingOut) {
                    console.log('Logout in progress, allowing auth page access')
                    authStore.state.isLoggingOut = false
                    next()
                    return
                }

                // NOTE: Email verification for creators is temporarily disabled
                // Uncomment the block below to re-enable
                // Check if user needs email verification (email/password auth only)
                // const currentUser = firebase.auth().currentUser
                // if (currentUser && !currentUser.emailVerified) {
                //     // Check if user authenticated with email/password (not OAuth)
                //     const usedEmailPassword = currentUser.providerData.some(
                //         (provider) => provider.providerId === 'password'
                //     )
                // }

                // Check if user is logged in and has backend data
                if (authStore.user && authStore.user.data) {
                    // Redirect to appropriate chats page based on user type
                    const userType = authStore.user.data.typeofuser
                    if (userType === 'Creator') {
                        next({ name: 'creator-chats' })
                    } else {
                        next({ name: 'client-chats' })
                    }
                } else {
                    // Not logged in, proceed to auth page
                    next()
                }
            },
        },
        // Email verification redirect handler
        {
            path: '/email-verification',
            name: 'email-verification',
            component: () => import('../views/Home.vue'),
            meta: {
                requiresAuth: false,
                noAuth: true,
            },
            beforeEnter: async (to, from, next) => {
                const authStore = useAuthStore()
                const currentUser = firebase.auth().currentUser

                // If no user, redirect to auth page
                if (!currentUser) {
                    console.log(
                        'No user on email-verification redirect, redirecting to auth'
                    )
                    next({ name: 'auth' })
                    return
                }

                // Reload user to get latest verification status
                try {
                    await authUtils.reloadUserAuthState()
                    const updatedUser = firebase.auth().currentUser

                    // NOTE: Email verification for creators is temporarily disabled
                    // Changed from: if (updatedUser && updatedUser.emailVerified)
                    // Check if email is verified (bypassed - treating all as verified)
                    if (updatedUser) {
                        // Fetch fresh user data from backend
                        const UserService = (
                            await import('@/services/api/user.service.js')
                        ).default
                        const user = await UserService.getCurrentUser()

                        if (user && user.status === 200 && user.data) {
                            authStore.user.data = user.data

                            // Wait for app initialization
                            await waitForAppInitialization(authStore)

                            // Check for pending client displayname from preview flow
                            const pendingDisplayname = localStorage.getItem(
                                'pendingClientDisplayname'
                            )
                            const targetCreatorId = localStorage.getItem(
                                'loginTargetCreatorId'
                            )
                            const targetCreatorUsername = localStorage.getItem(
                                'loginTargetCreatorUsername'
                            )

                            // Determine if this is a new user or existing user without setup
                            const isNewUser = !user.data.finishedSetup

                            // Handle preview flow auto-upgrade for new email signups
                            if (
                                pendingDisplayname &&
                                targetCreatorId &&
                                (isNewUser || !user.data.finishedSetup)
                            ) {
                                console.log(
                                    'Email verification: Auto-upgrading to client for preview flow'
                                )

                                try {
                                    // Auto-upgrade to client with pending displayname
                                    const username = pendingDisplayname
                                        .trim()
                                        .toLowerCase()
                                        .replace(/\s+/g, '-')
                                        .replace(/-+$/g, '')

                                    // Update displayname and username
                                    const updateResponse =
                                        await UserService.updateUser({
                                            displayname: pendingDisplayname,
                                            username: username,
                                        })

                                    if (updateResponse.status === 200) {
                                        // Small delay to avoid MongoDB write conflict
                                        await new Promise((resolve) =>
                                            setTimeout(resolve, 300)
                                        )

                                        // Upgrade to client
                                        await UserService.upgradeToClient()

                                        // Handle referral code if present
                                        const refCode = localStorage.getItem(
                                            'loginTargetCreatorRefCode'
                                        )
                                        if (refCode) {
                                            try {
                                                const referralApi = (
                                                    await import(
                                                        '@/services/api/referral.service'
                                                    )
                                                ).default
                                                await referralApi.createClientReferral(
                                                    refCode
                                                )
                                                localStorage.removeItem(
                                                    'loginTargetCreatorRefCode'
                                                )
                                            } catch (e) {
                                                console.warn(
                                                    'Referral failed:',
                                                    e
                                                )
                                            }
                                        }

                                        // Fetch fresh user data
                                        const freshUser =
                                            await UserService.getCurrentUser()
                                        if (
                                            freshUser?.status === 200 &&
                                            freshUser.data
                                        ) {
                                            authStore.user.data = freshUser.data
                                            authStore.user.data.typeofuser =
                                                'Client'
                                            authStore.user.data.finishedSetup = true
                                        }

                                        // Clean up displayname
                                        localStorage.removeItem(
                                            'pendingClientDisplayname'
                                        )

                                        // Create conversation with target creator
                                        if (targetCreatorUsername) {
                                            const conversationStore = (
                                                await import(
                                                    '@/stores/conversation.store'
                                                )
                                            ).useConversationStore()

                                            // Pre-populate usersMap with creator data from localStorage if available
                                            const previewCreatorDataStr =
                                                localStorage.getItem(
                                                    'previewCreatorData'
                                                )
                                            if (
                                                previewCreatorDataStr &&
                                                !conversationStore.state
                                                    .usersMap[targetCreatorId]
                                            ) {
                                                try {
                                                    const creatorData =
                                                        JSON.parse(
                                                            previewCreatorDataStr
                                                        )
                                                    conversationStore.state.usersMap[
                                                        targetCreatorId
                                                    ] = creatorData
                                                    console.log(
                                                        'Email verification: Pre-populated usersMap with creator data'
                                                    )
                                                } catch (e) {
                                                    console.warn(
                                                        'Failed to parse previewCreatorData:',
                                                        e
                                                    )
                                                }
                                            }

                                            await conversationStore.findOrCreateConversationWithUser(
                                                targetCreatorId,
                                                targetCreatorUsername
                                            )

                                            // Clean up creator info
                                            localStorage.removeItem(
                                                'loginTargetCreatorId'
                                            )
                                            localStorage.removeItem(
                                                'loginTargetCreatorUsername'
                                            )
                                            localStorage.removeItem(
                                                'previewCreatorData'
                                            )

                                            // Wait a bit for conversation to be ready
                                            await new Promise((resolve) =>
                                                setTimeout(resolve, 300)
                                            )
                                        }

                                        // Redirect to client chats
                                        next({ name: 'client-chats' })
                                        return
                                    }
                                } catch (error) {
                                    console.error(
                                        'Auto-upgrade failed in email verification:',
                                        error
                                    )
                                    // Fall through to normal flow
                                }
                            }

                            // For new users OR users who haven't finished setup
                            if (isNewUser || !user.data.finishedSetup) {
                                // Check if language has been explicitly selected
                                const hasLanguage = user.data?.uiLanguage
                                if (!hasLanguage) {
                                    next({ name: 'language-selection' })
                                    return
                                }
                                next({ name: 'account-creation' })
                                return
                            }

                            // For existing users who have finished setup
                            const userType = user.data.typeofuser
                            if (userType === 'Creator') {
                                next({ name: 'creator-chats' })
                                return
                            } else if (userType === 'Client') {
                                // Check for pending creator conversation (existing client login after preview)
                                if (targetCreatorId && targetCreatorUsername) {
                                    console.log(
                                        'Email verification: Creating conversation for existing client'
                                    )
                                    try {
                                        const conversationStore = (
                                            await import(
                                                '@/stores/conversation.store'
                                            )
                                        ).useConversationStore()

                                        // Pre-populate usersMap with creator data from localStorage if available
                                        const previewCreatorDataStr =
                                            localStorage.getItem(
                                                'previewCreatorData'
                                            )
                                        if (
                                            previewCreatorDataStr &&
                                            !conversationStore.state.usersMap[
                                                targetCreatorId
                                            ]
                                        ) {
                                            try {
                                                const creatorData = JSON.parse(
                                                    previewCreatorDataStr
                                                )
                                                conversationStore.state.usersMap[
                                                    targetCreatorId
                                                ] = creatorData
                                                console.log(
                                                    'Email verification (existing client): Pre-populated usersMap with creator data'
                                                )
                                            } catch (e) {
                                                console.warn(
                                                    'Failed to parse previewCreatorData:',
                                                    e
                                                )
                                            }
                                        }

                                        await conversationStore.findOrCreateConversationWithUser(
                                            targetCreatorId,
                                            targetCreatorUsername
                                        )

                                        // Clean up creator info
                                        localStorage.removeItem(
                                            'loginTargetCreatorId'
                                        )
                                        localStorage.removeItem(
                                            'loginTargetCreatorUsername'
                                        )
                                        localStorage.removeItem(
                                            'previewCreatorData'
                                        )
                                    } catch (error) {
                                        console.error(
                                            'Failed to create conversation in email verification:',
                                            error
                                        )
                                    }
                                }
                                next({ name: 'client-chats' })
                                return
                            } else if (userType === 'Admin') {
                                next({ name: 'admin-decaf-payouts' })
                                return
                            }
                        }

                        // Fallback: redirect to auth if we can't determine route
                        next({ name: 'auth' })
                    } else {
                        // Email not verified yet, show auth page with verification flow
                        next({ name: 'auth' })
                    }
                } catch (error) {
                    console.error(
                        'Error handling email verification redirect:',
                        error
                    )
                    next({ name: 'auth' })
                }
            },
        },
        // Auth redirect handler - Firebase redirects here after email verification
        {
            path: '/auth',
            name: 'auth-redirect',
            redirect: { name: 'email-verification' },
            meta: {
                requiresAuth: false,
                noAuth: true,
            },
        },
        // Landing page
        {
            path: '/category/nsfw',
            name: 'nsfw',
            component: () => import('../views/LandingPage.vue'),
            meta: {
                requiresAuth: false,
                noAuth: true,
            },
        },
        //creator
        {
            path: '/creator',
            name: 'creator-home',
            component: () => import('../views/CreatorHomeNew.vue'),
            meta: {
                view: 'creator',
                noAuth: true,
            },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore()

                // Check if user is logged in and has finished setup
                if (authStore.user?.data && authStore.user.data.finishedSetup) {
                    // If user is a Client, redirect to client-chats
                    if (authStore.user.data.typeofuser === 'Client') {
                        console.log(
                            'Client user visiting creator page, redirecting to client-chats'
                        )
                        next({ name: 'client-chats' })
                        return
                    }

                    // If user is a Creator, redirect to creator-chats
                    if (authStore.user.data.typeofuser === 'Creator') {
                        next({ name: 'creator-chats' })
                        return
                    }
                }

                // Otherwise, proceed to creator home
                next()
            },
        },
        {
            path: '/creator/pwa-setup',
            name: 'creator-pwa-setup',
            component: () => import('../views/CreatorPWASetup.vue'),
            meta: {
                view: 'creator',
            },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore()

                // Check if user is logged in
                if (!authStore.user?.uid) {
                    next({ name: 'auth' })
                    return
                }

                // Check if language has been selected (check authStore first, then localStorage as fallback)
                const hasLanguageInStore = authStore.user?.data?.uiLanguage
                const hasLanguageInStorage =
                    localStorage.getItem('preferredLanguage')
                const hasLanguage = hasLanguageInStore || hasLanguageInStorage

                if (!hasLanguage) {
                    console.log(
                        'Language not selected, redirecting to language-selection'
                    )
                    next({ name: 'language-selection' })
                    return
                }

                // If language exists in localStorage but not in store, update the store
                if (
                    !hasLanguageInStore &&
                    hasLanguageInStorage &&
                    authStore.user?.data
                ) {
                    authStore.user.data.uiLanguage = hasLanguageInStorage
                }

                next()
            },
        },
        {
            path: '/creator/profile-onboarding',
            name: 'creator-profile-onboarding',
            component: () => import('../views/CreatorProfileOnboarding.vue'),
            meta: {
                view: 'creator',
            },
            redirect: {
                name: 'creator-profile-onboarding-stage',
                params: { stage: 'need-display-name' },
            },
            children: [
                {
                    path: ':stage',
                    name: 'creator-profile-onboarding-stage',
                    component: () =>
                        import(
                            '../views/onboarding/OnboardingStageWrapper.vue'
                        ),
                },
            ],
        },
        {
            path: '/creator/chats',
            name: 'creator-chats',
            component: () => import('../views/Chats.vue'),
            meta: {
                view: 'creator',
                keepAlive: true,
            },
            beforeEnter: async (to, from, next) => {
                // If push-notification appended ?senderId, open that conversation first
                if (to.query.senderId) {
                    try {
                        const { useConversationStore } = await import(
                            '@/stores/conversation.store'
                        )
                        const convStore = useConversationStore()
                        await convStore.findOrCreateConversationWithUser(
                            to.query.senderId,
                            null
                        )
                    } catch (err) {
                        console.error(
                            'Failed to pre-open conversation from senderId',
                            err
                        )
                    }
                    const cleanQuery = { ...to.query }
                    delete cleanQuery.senderId
                    return next({ ...to, query: cleanQuery })
                }

                const authStore = useAuthStore()

                // Check if user has finished setup
                if (
                    authStore.user?.data &&
                    !authStore.user.data.finishedSetup
                ) {
                    console.log(
                        'Creator has not finished setup, redirecting to account creation'
                    )
                    next({ name: 'account-creation' })
                    return
                }

                next()
            },
        },
        {
            path: '/creator/my-account',
            name: 'creator-my-account',
            component: () => import('../views/MyAccount.vue'),
            meta: {
                view: 'creator',
            },
        },
        {
            path: '/creator/my-services',
            name: 'creator-my-services',
            component: () => import('../views/MyServices.vue'),
            meta: {
                view: 'creator',
            },
        },
        {
            path: '/creator/my-money',
            name: 'creator-my-money',
            component: () => import('../views/Creator/MyTransactions.vue'),
            meta: {
                view: 'creator',
            },
        },
        {
            path: '/creator/my-payouts',
            name: 'creator-my-payouts',
            component: () => import('../views/Creator/MyPayouts.vue'),
            meta: {
                view: 'creator',
            },
        },
        {
            path: '/creator/my-content',
            name: 'creator-my-content',
            component: () => import('../views/MyContent.vue'),
            meta: {
                view: 'creator',
            },
        },
        {
            path: '/language-selection',
            name: 'language-selection',
            component: () => import('../views/LanguageSelection.vue'),
            meta: {
                requiresAuth: true,
                noAuth: false,
            },
            beforeEnter: (to, from, next) => {
                const authStore = useAuthStore()

                // Check if user is logged in
                if (!authStore.user?.uid) {
                    console.log(
                        'No user on language-selection page, redirecting to auth'
                    )
                    next({ name: 'auth' })
                    return
                }

                // Check if user is already set up - if so, redirect appropriately
                if (authStore.user?.data?.finishedSetup) {
                    if (authStore.user.data.typeofuser === 'Client') {
                        console.log(
                            'Client already set up, redirecting to client-chats'
                        )
                        next({ name: 'client-chats' })
                        return
                    }

                    if (authStore.user.data.typeofuser === 'Creator') {
                        next({ name: 'creator-chats' })
                        return
                    }
                }

                // Check if language has already been explicitly selected (using authStore instead of localStorage)
                const hasLanguage = authStore.user?.data?.uiLanguage
                if (hasLanguage) {
                    console.log(
                        'Language already selected, redirecting to account-creation'
                    )
                    next({ name: 'account-creation' })
                    return
                }

                // User needs to select language, allow access
                next()
            },
        },
        {
            path: '/account-creation',
            name: 'account-creation',
            component: () => import('../views/AccountCreation.vue'),
            meta: {
                view: 'client', // Default to client view
            },
            beforeEnter: async (to, from, next) => {
                const authStore = useAuthStore()

                // Check if user is logged in
                if (!authStore.user?.uid) {
                    console.log(
                        'No user on account-creation page, redirecting to auth'
                    )
                    next({ name: 'auth' })
                    return
                }

                // Check if language has been selected (check authStore first, then localStorage as fallback)
                const hasLanguageInStore = authStore.user?.data?.uiLanguage
                const hasLanguageInStorage =
                    localStorage.getItem('preferredLanguage')
                const hasLanguage = hasLanguageInStore || hasLanguageInStorage

                if (!hasLanguage) {
                    console.log(
                        'Language not selected, redirecting to language-selection'
                    )
                    next({ name: 'language-selection' })
                    return
                }

                // If language exists in localStorage but not in store, update the store
                if (
                    !hasLanguageInStore &&
                    hasLanguageInStorage &&
                    authStore.user?.data
                ) {
                    authStore.user.data.uiLanguage = hasLanguageInStorage
                }

                // Check if user is already set up
                if (authStore.user?.data?.finishedSetup) {
                    // If it's a client, redirect to client-chats
                    if (authStore.user.data.typeofuser === 'Client') {
                        console.log(
                            'Client already set up, redirecting to client-chats'
                        )
                        next({ name: 'client-chats' })
                        return
                    }

                    // If it's a creator, redirect to creator-chats
                    if (authStore.user.data.typeofuser === 'Creator') {
                        next({ name: 'creator-chats' })
                        return
                    }
                }

                // Check signupIntent for auto-upgrade (single source of truth)
                // Set by entry points: CreatorHomeNew (creator), PreviewChat (client)
                const {
                    getSignupData,
                    upgradeUserToCreator,
                    upgradeUserToClient,
                    clearSignupLocalStorage,
                } = await import('@/utils/accountUpgrade.js')

                const signupData = getSignupData(
                    new URLSearchParams(window.location.search)
                )

                // Auto-upgrade to Creator if signupIntent is 'creator'
                if (signupData.signupIntent === 'creator') {
                    console.log(
                        'Account-creation guard: Auto-upgrading to Creator'
                    )
                    try {
                        const response = await upgradeUserToCreator({
                            studioInviteToken: signupData.studioInviteToken,
                            partnerUsername: signupData.partnerUsername,
                            // Phone not passed - will be collected in AccountCreation
                        })

                        if (response.status === 200) {
                            // Update auth store with new user data
                            await authStore.updateUserData(response.data)

                            // Clear signup flow data (keep studioInvite cleared by utility)
                            clearSignupLocalStorage('Creator')

                            // Continue to AccountCreation page which will show WhatsApp step
                            // for phone collection since user is now a Creator without phone
                            console.log(
                                'Creator auto-upgrade successful, proceeding to phone collection'
                            )
                            next()
                            return
                        }
                    } catch (error) {
                        console.error('Creator auto-upgrade failed:', error)
                        // Clear intent and fall through to show AccountCreation page
                        clearSignupLocalStorage('Creator')
                    }
                }

                // Auto-upgrade to Client if signupIntent is 'client'
                if (signupData.signupIntent === 'client') {
                    console.log(
                        'Account-creation guard: Auto-upgrading to Client'
                    )
                    try {
                        const result = await upgradeUserToClient({
                            displayname: signupData.pendingDisplayname,
                            targetCreatorId: signupData.targetCreatorId,
                            targetCreatorUsername:
                                signupData.targetCreatorUsername,
                            refCode: signupData.refCode,
                            authStore: authStore,
                        })

                        if (result.success) {
                            // Clear signup flow data
                            clearSignupLocalStorage('Client')

                            console.log(
                                'Client auto-upgrade successful, redirecting to chats'
                            )
                            next({ name: 'client-chats', replace: true })
                            return
                        }
                    } catch (error) {
                        console.error('Client auto-upgrade failed:', error)
                        // Clear intent and fall through to show AccountCreation page
                        clearSignupLocalStorage('Client')
                    }
                }

                // No auto-upgrade - show AccountCreation page for manual selection
                next()
            },
        },
        //Client
        {
            path: '/client/chats',
            name: 'client-chats',
            component: () => import('../views/Chats.vue'),
            meta: {
                view: 'client',
                keepAlive: true,
                order: 0,
            },
            beforeEnter: async (to, from, next) => {
                if (to.query.senderId) {
                    try {
                        const { useConversationStore } = await import(
                            '@/stores/conversation.store'
                        )
                        const convStore = useConversationStore()
                        await convStore.findOrCreateConversationWithUser(
                            to.query.senderId,
                            null
                        )
                    } catch (err) {
                        console.error(
                            'Failed to pre-open conversation from senderId',
                            err
                        )
                    }
                    const cleanQuery = { ...to.query }
                    delete cleanQuery.senderId
                    return next({ ...to, query: cleanQuery })
                }

                const authStore = useAuthStore()

                // Check if user is logged in
                if (!authStore.user?.data) {
                    next({ name: 'auth' })
                    return
                }

                // Check for preview flow auto-upgrade BEFORE checking finishedSetup
                // This handles the case where the old tab gets redirected here
                const pendingDisplayname = localStorage.getItem(
                    'pendingClientDisplayname'
                )
                const targetCreatorId = localStorage.getItem(
                    'loginTargetCreatorId'
                )
                const targetCreatorUsername = localStorage.getItem(
                    'loginTargetCreatorUsername'
                )

                if (
                    pendingDisplayname &&
                    targetCreatorId &&
                    !authStore.user.data.finishedSetup
                ) {
                    console.log(
                        'Client-chats: Preview flow detected, performing auto-upgrade'
                    )

                    try {
                        const UserService = (
                            await import('@/services/api/user.service.js')
                        ).default
                        const username = pendingDisplayname
                            .trim()
                            .toLowerCase()
                            .replace(/\s+/g, '-')
                            .replace(/-+$/g, '')

                        await UserService.updateUser({
                            displayname: pendingDisplayname,
                            username: username,
                        })

                        await new Promise((resolve) => setTimeout(resolve, 300))
                        await UserService.upgradeToClient()

                        // Handle referral if present
                        const refCode = localStorage.getItem(
                            'loginTargetCreatorRefCode'
                        )
                        if (refCode) {
                            try {
                                const referralApi = (
                                    await import(
                                        '@/services/api/referral.service'
                                    )
                                ).default
                                await referralApi.createClientReferral(refCode)
                                localStorage.removeItem(
                                    'loginTargetCreatorRefCode'
                                )
                            } catch (e) {
                                console.warn('Referral failed:', e)
                            }
                        }

                        // Fetch fresh user data
                        const freshUser = await UserService.getCurrentUser()
                        if (freshUser?.status === 200 && freshUser.data) {
                            authStore.user.data = freshUser.data
                            authStore.user.data.typeofuser = 'Client'
                            authStore.user.data.finishedSetup = true
                        }

                        // Clean up
                        localStorage.removeItem('pendingClientDisplayname')

                        // Create conversation will be handled below
                        // Don't return here, let the rest of the guard run
                    } catch (error) {
                        console.error(
                            'Auto-upgrade failed in client-chats guard:',
                            error
                        )

                        // Check if error is because user is already a Client (from another tab/session)
                        const errorMessage =
                            error?.response?.data?.message ||
                            error?.message ||
                            ''
                        if (
                            errorMessage.toLowerCase().includes('already') &&
                            errorMessage.toLowerCase().includes('client')
                        ) {
                            console.log(
                                'User is already a Client (upgraded in another tab), fetching fresh data'
                            )

                            try {
                                const UserService = (
                                    await import(
                                        '@/services/api/user.service.js'
                                    )
                                ).default
                                const freshUser =
                                    await UserService.getCurrentUser()
                                if (
                                    freshUser?.status === 200 &&
                                    freshUser.data
                                ) {
                                    authStore.user.data = freshUser.data
                                    console.log(
                                        'Refreshed user data, finishedSetup:',
                                        freshUser.data.finishedSetup
                                    )
                                }

                                // Clean up the pending displayname since user is already upgraded
                                localStorage.removeItem(
                                    'pendingClientDisplayname'
                                )

                                // Continue with conversation creation below
                            } catch (refreshError) {
                                console.error(
                                    'Failed to refresh user data:',
                                    refreshError
                                )
                            }
                        }
                        // Fall through to the finishedSetup check which will redirect to account-creation if needed
                    }
                }

                // Check if user has finished setup
                if (
                    authStore.user?.data &&
                    !authStore.user.data.finishedSetup
                ) {
                    console.log(
                        'Client has not finished setup, redirecting to account creation'
                    )
                    next({ name: 'account-creation' })
                    return
                }

                // Check for post-signup conversation target (from preview flow)
                // This ensures the conversation is ready even if auto-upgrade had issues
                // Note: targetCreatorId and targetCreatorUsername are already defined above
                if (targetCreatorId && targetCreatorUsername) {
                    console.log(
                        'Post-signup/login target detected. Ensuring conversation is ready...'
                    )
                    try {
                        const { useConversationStore } = await import(
                            '@/stores/conversation.store'
                        )
                        const convStore = useConversationStore()

                        // Pre-populate usersMap with creator data from localStorage if available
                        const previewCreatorDataStr =
                            localStorage.getItem('previewCreatorData')
                        if (
                            previewCreatorDataStr &&
                            !convStore.state.usersMap[targetCreatorId]
                        ) {
                            try {
                                const creatorData = JSON.parse(
                                    previewCreatorDataStr
                                )
                                convStore.state.usersMap[targetCreatorId] =
                                    creatorData
                                console.log(
                                    'Pre-populated usersMap with creator data from localStorage'
                                )
                            } catch (e) {
                                console.warn(
                                    'Failed to parse previewCreatorData:',
                                    e
                                )
                            }
                        }

                        // Ensure conversation exists and is set as current
                        const success =
                            await convStore.findOrCreateConversationWithUser(
                                targetCreatorId,
                                targetCreatorUsername
                            )

                        if (success) {
                            console.log(
                                'Conversation ready, current conversation:',
                                convStore.state.currentConversation?._id
                            )
                            // Small delay to ensure conversation is fully set as current
                            await new Promise((resolve) =>
                                setTimeout(resolve, 300)
                            )

                            // Clean up localStorage now that conversation is confirmed
                            // Only clean up if conversation was successfully created
                            localStorage.removeItem('loginTargetCreatorId')
                            localStorage.removeItem(
                                'loginTargetCreatorUsername'
                            )
                            localStorage.removeItem('previewCreatorData')
                        } else {
                            console.warn(
                                'Failed to find/create target conversation, keeping localStorage for Chats.vue retry'
                            )
                            // Don't clean up - let Chats.vue component try to create the conversation
                        }

                        // No need to wait for app initialization - it's already happening in background
                        // The conversation store is initialized and functional
                    } catch (err) {
                        console.error(
                            'Failed to ensure conversation in router guard',
                            err
                        )
                        // Don't clean up on error - let Chats.vue component try to create the conversation
                        console.log(
                            'Keeping localStorage flags for Chats.vue to retry conversation creation'
                        )
                    }
                }

                next()
            },
        },
        {
            path: '/client/my-account',
            name: 'client-my-account',
            component: () => import('../views/MyAccount.vue'),
            meta: {
                view: 'client',
            },
        },
        {
            path: '/client/my-content',
            name: 'client-my-content',
            component: () => import('../views/MyContent.vue'),
            meta: {
                view: 'client',
            },
        },
        {
            path: '/client/my-transactions',
            name: 'client-my-transactions',
            component: () => import('../views/Client/MyTransactions.vue'),
            meta: {
                view: 'client',
            },
        },
        {
            path: '/preview-chat',
            name: 'preview-chat',
            component: () => import('../views/PreviewChat.vue'),
            meta: {
                view: 'client',
                noAuth: true,
                requiresAuth: false,
            },
        },
        {
            path: '/client/profiles',
            name: 'client-profiles',
            component: () => import('../views/CreatorListing.vue'),
            meta: {
                view: 'client',
                noAuth: true,
                order: 1,
            },
        },
        // Admin routes with shared layout
        {
            path: '/admin',
            component: () => import('../layouts/AdminLayout.vue'),
            meta: {
                view: 'admin',
                requiresAdmin: true,
            },
            children: [
                {
                    path: '',
                    redirect: '/admin/creators',
                },
                {
                    path: 'current-calls',
                    name: 'admin-current-calls',
                    component: () => import('../views/Admin/CurrentCalls.vue'),
                },
                {
                    path: 'decaf-payouts',
                    name: 'admin-decaf-payouts',
                    component: () => import('../views/Admin/DecafPayouts.vue'),
                },
                {
                    path: 'creators',
                    name: 'admin-creators',
                    component: () => import('../views/Admin/Creators.vue'),
                },
                {
                    path: 'clients',
                    name: 'admin-clients',
                    component: () => import('../views/Admin/Clients.vue'),
                },
                {
                    path: 'conversations',
                    name: 'admin-conversations',
                    component: () => import('../views/Admin/Conversations.vue'),
                },
                {
                    path: 'transactions',
                    name: 'admin-transactions',
                    component: () => import('../views/Admin/Transactions.vue'),
                },
            ],
        },
        // Compliance route
        {
            path: '/compliance/takedown-monitoring',
            name: 'compliance',
            component: () => import('../views/Compliance.vue'),
            meta: {
                noAuth: true,
            },
        },
        //Legal
        {
            path: '/legal/terms-of-use',
            name: 'legal-terms-of-use',
            component: () => import('../views/Legal/TermsOfUse.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/privacy-policy',
            name: 'legal-privacy-policy',
            component: () => import('../views/Legal/PrivacyPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/custodian-records-and-age-verification',
            name: 'legal-custodian-records-and-age-verification',
            component: () =>
                import('../views/Legal/CustodianRecordsAndAgeVerification.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/dmca-takedown-policy',
            name: 'legal-dmca-takedown-policy',
            component: () => import('../views/Legal/DMCATakedownPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/vat-tax-section',
            name: 'legal-vat-tax-section',
            component: () => import('../views/Legal/VATTaxSection.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/appeals-policy',
            name: 'legal-appeals-policy',
            component: () => import('../views/Legal/AppealsPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/referral-program-terms',
            name: 'legal-referral-program-terms',
            component: () => import('../views/Legal/ReferralProgramTerms.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/platform-to-business-terms',
            name: 'legal-platform-to-business-terms',
            component: () =>
                import('../views/Legal/PlattformToBusinessTerms.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/complaints-policy',
            name: 'legal-complaints-policy',
            component: () => import('../views/Legal/ComplaintsPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/fan-creator-agreement',
            name: 'legal-fan-creator-agreement',
            component: () => import('../views/Legal/FanCreatorAgreement.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        // {
        //     path: '/legal/fan-creator-agreement-studio',
        //     name: 'legal-fan-creator-agreement-studio',
        //     component: () =>
        //         import('../views/Legal/FanCreatorAgreementStudio.vue'),
        //     meta: {
        //         view: 'legal',
        //         noAuth: true,
        //     },
        // },
        {
            path: '/legal/acceptable-use-policy',
            name: 'legal-acceptable-use-policy',
            component: () => import('../views/Legal/AcceptibleUsePolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/content-management-policy',
            name: 'legal-content-management-policy',
            component: () =>
                import('../views/Legal/ContentManagementPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/chargeback-fraud-mitigation-policy',
            name: 'legal-chargeback-fraud-mitigation-policy',
            component: () =>
                import('../views/Legal/ChargebackFraudMitigationPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/return-refund-policy',
            name: 'legal-return-refund-policy',
            component: () => import('../views/Legal/ReturnRefundPolicy.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/anti-modern-slavery-declaration',
            name: 'legal-anti-modern-slavery-declaration',
            component: () =>
                import('../views/Legal/AntiModernSlaveryDeclaration.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/partners-agreement',
            name: 'legal-partners-agreement',
            component: () => import('../views/Legal/PartnersAgreement.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/penalties',
            name: 'legal-penalties',
            component: () => import('../views/Legal/Penalties.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        {
            path: '/legal/content-permission',
            name: 'legal-content-permission',
            component: () => import('../views/Legal/ContentPermission.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        //Legal Agreements: superStudio
        // {
        //     path: '/legal/agreements/superStudio',
        //     name: 'legal-super-studio-agreement',
        //     component: () =>
        //         import('../views/Legal/agreements/superstudioAgreement.vue'),
        //     meta: {
        //         view: 'legal',
        //         noAuth: true,
        //     },
        // },
        //Legal Agreements: creator-studio
        {
            path: '/legal/agreements/creator-studio',
            name: 'legal-creator-studio-agreement',
            component: () =>
                import('../views/Legal/agreements/creatorStudioAgreement.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        //Legal Agreements: creator
        {
            path: '/legal/agreements/creator',
            name: 'legal-creator-agreement',
            component: () =>
                import('../views/Legal/agreements/creatorAgreement.vue'),
            meta: {
                view: 'legal',
                noAuth: true,
            },
        },
        //Partners
        {
            path: '/partners',
            name: 'partners',
            component: () => import('../views/Partners.vue'),
            meta: {
                view: 'partners',
                noAuth: true,
            },
        },
        {
            path: '/:slug',
            name: 'creator-profile',
            component: () => import('../views/CreatorProfileShare.vue'),
            meta: {
                noAuth: true,
                order: 2,
            },
        },
        // Catch-all 404 route - must be last
        {
            path: '/:pathMatch(.*)*',
            name: 'not-found',
            component: () => import('../views/NotFound.vue'),
            meta: {
                noAuth: true,
            },
        },
    ],
})

// Global guard to persist studio invite tokens
router.beforeEach((to, from, next) => {
    if (to.query.studioInvite) {
        try {
            localStorage.setItem('studioInvite', to.query.studioInvite)
        } catch (e) {
            console.warn('Failed to persist studio invite token:', e)
        }
    }
    next()
})

router.onError((error) => {
    if (isStaleModuleImportError(error)) {
        try {
            console.warn(
                '[router] Detected stale module import. Forcing reload...',
                error?.message || error
            )
        } catch (_) {}
        forceReloadDueToStaleAssets({
            reason: 'router-chunk-load-failed',
        })
    }
})

// Track page views manually to bypass adblockers (which block the SDK autocapture)
// Restricted to once per session (acting as a "Session Start" / "Visit" event)
router.afterEach((to) => {
    try {
        const SESSION_TRACK_KEY = 'has_tracked_session_pageview'
        if (!sessionStorage.getItem(SESSION_TRACK_KEY)) {
            sessionStorage.setItem(SESSION_TRACK_KEY, 'true')
            trackEvent('Page View', {
                path: to.path,
                name: to.name,
                params: to.params,
                query: to.query,
            })
        }
    } catch (_) {}
})

export default router
